// ANANT KHATA — Daily ledger for Households (B2C) + Vendors (B2B)
// Strictly NO Affiliate Network. Direct 50 AT referral. 30d household / 15d vendor trial.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Users, Wallet, ShieldCheck, RefreshCw, Home, Store, Plus, Trash2,
  Copy, MessageCircle, Calendar, Check, X, TrendingUp, ClipboardList, PauseCircle, Download, Share2, Facebook, Twitter,
} from 'lucide-react';
import ATWalletBadge from '../../components/ATWalletBadge';
import api from '../../utils/api';

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthISO = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// Universal share helper — uses Web Share API on mobile with fallbacks
const shareEverywhere = async (title, text, url) => {
  const payload = { title, text: `${text}\n${url}` };
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
      toast.success('Shared');
      return;
    }
  } catch (_) { /* user cancelled */ }
  navigator.clipboard.writeText(payload.text).then(() => toast.success('Copied to clipboard — paste anywhere'));
};

const shareTargets = (title, body, url) => ({
  whatsapp: `https://wa.me/?text=${encodeURIComponent(body + '\n' + url)}`,
  telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(body)}`,
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(body)}`,
  twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(body)}&url=${encodeURIComponent(url)}`,
});

export default function AnantKhata() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [searchParams] = useSearchParams();
  const refFromURL = searchParams.get('ref') || '';

  // Role setup form (first-time)
  const [roleForm, setRoleForm] = useState({ role: 'household', vendor_category: 'milk', shop_name: '', phone: '', referred_by: refFromURL });
  const [savingRole, setSavingRole] = useState(false);

  useEffect(() => { const t = setInterval(() => setNowTick(Date.now()), 1000); return () => clearInterval(t); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/anant-khata/config');
      setConfig(r.data);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const countdown = useMemo(() => {
    const exp = config?.subscription?.expires_at;
    if (!exp) return null;
    const ms = new Date(exp).getTime() - nowTick;
    if (ms <= 0) return { expired: true, dd: 0, hh: 0, mm: 0, ss: 0 };
    const s = Math.floor(ms / 1000);
    return { expired: false, dd: Math.floor(s / 86400), hh: Math.floor((s % 86400) / 3600), mm: Math.floor((s % 3600) / 60), ss: s % 60 };
  }, [config, nowTick]);
  const fmt2 = (n) => String(n).padStart(2, '0');

  const activeSub = config?.subscription?.active && !(countdown && countdown.expired);
  const isTrial = config?.subscription?.is_trial;
  const role = config?.subscription?.role || config?.profile?.role;
  const hasRole = !!config?.profile?.role;

  const saveRole = async () => {
    setSavingRole(true);
    try {
      await api.post('/anant-khata/select-role', roleForm);
      toast.success(`Welcome! ${roleForm.role === 'household' ? '30-day' : '15-day'} free trial activated`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSavingRole(false); }
  };

  const subscribe = async (planId) => {
    if (!window.confirm(`Subscribe to ${planId}?`)) return;
    setSubscribing(true);
    try {
      const r = await api.post('/anant-khata/subscribe', { plan_id: planId });
      toast.success(r.data?.message || 'Subscribed!');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Subscribe failed'); }
    finally { setSubscribing(false); }
  };

  const copyReferral = () => {
    const link = `${window.location.origin}/anant-khata?ref=${config?.profile?.username || ''}`;
    navigator.clipboard.writeText(link).then(() => toast.success('Referral link copied!'));
  };
  const shareWhatsApp = () => {
    const link = `${window.location.origin}/anant-khata`;
    const text = `📒 *Anant Khata* — Digital daily ledger for ghar aur vyapar. 30 din free trial mila hai. Refer karo, 50 AT bonus pao!\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const plans = config?.plans || [];
  const showRoleForm = !hasRole;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-amber-950 via-slate-900 to-black text-white">
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-amber-400/50 px-4 py-2 flex items-center justify-between shadow-2xl shadow-black">
        <Link to="/" className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><span>← Home</span></Link>
        <ATWalletBadge compact />
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950 via-orange-900/40 to-red-950 p-6 mb-6" data-testid="khata-hero">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-500/20 blur-3xl pointer-events-none animate-pulse"></div>
          <div className="relative text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500/25 to-orange-500/25 border border-amber-400/40 text-[10px] font-black tracking-widest text-amber-200 mb-3">
              <Sparkles className="w-3 h-3 animate-pulse" /> ANANT KHATA · 30 DAY FREE TRIAL
            </div>
            <h1 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-amber-300 via-orange-200 to-yellow-300 bg-clip-text text-transparent drop-shadow-2xl">अनंत खाता</h1>
            <p className="text-sm text-slate-200 mt-2 max-w-2xl mx-auto">Ghar ka khaata + Vyapar ka khaata — dono ek jagah. Milkman, tiffin, laundry vendors ke liye <b className="text-amber-300">route management</b>. Family ke liye <b className="text-orange-300">daily services tracking</b> + <b className="text-yellow-300">50 AT referral bonus</b>.</p>
            <div className="flex justify-center gap-2 mt-4 flex-wrap">
              <div className="px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-400/30 text-[10px] font-black text-amber-200">🏠 Ghar Ka Khata</div>
              <div className="px-3 py-1.5 rounded-lg bg-orange-500/15 border border-orange-400/30 text-[10px] font-black text-orange-200">🏪 Vyapar Khata</div>
              <div className="px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-400/30 text-[10px] font-black text-emerald-200">💰 50 AT Referral</div>
              <div className="px-3 py-1.5 rounded-lg bg-slate-500/15 border border-slate-400/30 text-[10px] font-black text-slate-200">🚫 No Affiliate Network</div>
            </div>
          </div>
        </div>

        {/* Role setup */}
        {showRoleForm && (
          <div className="rounded-3xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/40 via-slate-900 to-orange-950/40 p-6 mb-6" data-testid="khata-role-setup">
            <div className="text-center mb-4">
              <div className="text-xl font-black text-amber-200">Aap kaun hain?</div>
              <div className="text-xs opacity-80 mt-1">Role select karo — trial turant activate ho jayegi</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <button onClick={() => setRoleForm({ ...roleForm, role: 'household' })} data-testid="khata-role-household" className={`rounded-2xl border-2 p-4 text-left transition-all ${roleForm.role === 'household' ? 'border-amber-400 bg-amber-500/15 scale-[1.02]' : 'border-slate-700 bg-slate-900/60 hover:border-amber-400/60'}`}>
                <Home className="w-6 h-6 text-amber-300 mb-2" />
                <div className="text-sm font-black">Ghar Ka Khata (Household)</div>
                <div className="text-[11px] opacity-80 mt-1">Milk, maid, newspaper tracking · Monthly budget analytics · 30 days FREE trial</div>
                <div className="text-[10px] font-black text-amber-300 mt-2">100 AT / month</div>
              </button>
              <button onClick={() => setRoleForm({ ...roleForm, role: 'vendor' })} data-testid="khata-role-vendor" className={`rounded-2xl border-2 p-4 text-left transition-all ${roleForm.role === 'vendor' ? 'border-orange-400 bg-orange-500/15 scale-[1.02]' : 'border-slate-700 bg-slate-900/60 hover:border-orange-400/60'}`}>
                <Store className="w-6 h-6 text-orange-300 mb-2" />
                <div className="text-sm font-black">Vyapar Khata (Vendor)</div>
                <div className="text-[11px] opacity-80 mt-1">Milkman · Tiffin · Laundry · Bulk delivery · WhatsApp bills · 15 days FREE trial</div>
                <div className="text-[10px] font-black text-orange-300 mt-2">1000 AT / month or 10,000 AT / year</div>
              </button>
            </div>
            {roleForm.role === 'vendor' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                <select value={roleForm.vendor_category} onChange={(e) => setRoleForm({ ...roleForm, vendor_category: e.target.value })} data-testid="khata-vendor-cat" className="px-2 py-2 rounded bg-slate-800 border border-slate-700 text-xs">
                  {(config?.vendor_categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <input placeholder="Shop / Business Name" value={roleForm.shop_name} onChange={(e) => setRoleForm({ ...roleForm, shop_name: e.target.value })} data-testid="khata-shop-name" className="px-2 py-2 rounded bg-slate-800 border border-slate-700 text-xs" />
                <input placeholder="Your Phone (10 digit)" value={roleForm.phone} onChange={(e) => setRoleForm({ ...roleForm, phone: e.target.value })} data-testid="khata-phone" className="px-2 py-2 rounded bg-slate-800 border border-slate-700 text-xs" />
              </div>
            )}
            {refFromURL && <div className="text-[10px] text-amber-200 mb-2">🎁 Referred by <b>{refFromURL}</b> — they will earn 50 AT bonus on your first paid subscription</div>}
            <button onClick={saveRole} disabled={savingRole} data-testid="khata-role-submit" className="w-full px-3 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-black text-sm font-black disabled:opacity-40">
              {savingRole ? 'Activating…' : `🚀 Activate ${roleForm.role === 'household' ? '30-day' : '15-day'} Free Trial`}
            </button>
          </div>
        )}

        {/* Subscription/Trial header */}
        {hasRole && activeSub && (
          <div className={`rounded-2xl border-2 ${isTrial ? 'border-amber-400/40 bg-amber-500/10' : 'border-emerald-400/40 bg-emerald-500/10'} p-3 mb-4 flex items-center justify-between flex-wrap gap-2`} data-testid="khata-sub-badge">
            <div>
              <div className={`text-[10px] uppercase tracking-widest font-black ${isTrial ? 'text-amber-300' : 'text-emerald-300'}`}>
                {isTrial ? `🆓 FREE TRIAL · ${config.trial_days[role]} DAYS` : '✅ ACTIVE SUBSCRIPTION'}
              </div>
              <div className="text-sm mt-0.5">
                <b>{config.subscription.plan_label}</b>
                {countdown && !countdown.expired && (
                  <> · <span className="font-mono text-amber-200" data-testid="khata-countdown">{fmt2(countdown.dd)}d : {fmt2(countdown.hh)}h : {fmt2(countdown.mm)}m : {fmt2(countdown.ss)}s left</span></>
                )}
              </div>
            </div>
            <a href="#khata-plans" className="text-xs px-3 py-1 rounded bg-amber-500 text-black font-black" data-testid="khata-upgrade-jump">
              {isTrial ? 'Upgrade Now →' : 'Extend / Renew →'}
            </a>
          </div>
        )}

        {/* Plans (always visible after role) */}
        {hasRole && (
          <div id="khata-plans" className="rounded-3xl border-2 border-amber-400/30 bg-gradient-to-br from-amber-950/30 via-slate-900 to-orange-950/30 p-5 mb-6" data-testid="khata-plans-panel">
            <div className="text-center mb-3">
              <div className="text-lg font-black text-amber-200">💎 Subscription Plans</div>
              <div className="text-[11px] opacity-70 mt-0.5">Trial expiry ke baad auto-deduct hoga · Referral se 50 AT bonus apne aap use hoga pehle</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {plans.filter((p) => role ? p.role === role : true).map((p) => (
                <div key={p.id} className={`rounded-2xl border-2 ${p.id.includes('yearly') ? 'border-amber-400 bg-amber-500/10' : 'border-orange-400/40 bg-orange-500/5'} p-4 text-center`} data-testid={`khata-plan-${p.id}`}>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">{p.label}</div>
                  <div className="text-3xl font-black mt-1">{p.amount} <span className="text-sm opacity-70">AT</span></div>
                  <div className="text-[10px] opacity-70 mt-0.5">{p.days} days</div>
                  <button onClick={() => subscribe(p.id)} disabled={subscribing} data-testid={`khata-subscribe-${p.id}`} className={`w-full mt-3 px-3 py-2 rounded-lg font-black text-sm ${p.id.includes('yearly') ? 'bg-gradient-to-r from-amber-400 to-yellow-300 text-black' : 'bg-gradient-to-r from-orange-500 to-red-500 text-white'} disabled:opacity-40`}>
                    {subscribing ? 'Processing…' : `Subscribe · ${p.amount} AT`}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referral strip */}
        {hasRole && (
          <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-3 mb-4 flex items-center justify-between flex-wrap gap-2" data-testid="khata-referral">
            <div className="text-xs">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">💰 Refer & Earn — {config?.referral_bonus_at || 50} AT per signup</div>
              <div className="opacity-90">Earned so far: <b className="text-emerald-300">{(config?.profile?.referral_earnings || 0).toFixed(2)} AT</b> — auto applied to your next subscription</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={copyReferral} data-testid="khata-copy-ref" className="px-2 py-1 rounded-lg bg-white/10 text-xs font-black flex items-center gap-1"><Copy className="w-3 h-3" /> Copy Link</button>
              <button onClick={shareWhatsApp} data-testid="khata-share-wa" className="px-2 py-1 rounded-lg bg-green-500 text-black text-xs font-black flex items-center gap-1"><MessageCircle className="w-3 h-3" /> WhatsApp</button>
              <button
                onClick={() => {
                  const link = `${window.location.origin}/anant-khata?ref=${config?.profile?.username || ''}`;
                  const body = `📒 Anant Khata — Digital daily ledger! ${role === 'vendor' ? 'Milkmen/tiffin/laundry vendors' : 'Ghar ka kharcha tracking'}. 30-day free trial + 50 AT bonus.`;
                  const t = shareTargets('Anant Khata', body, link);
                  window.open(t.telegram, '_blank');
                }}
                data-testid="khata-share-tg"
                className="px-2 py-1 rounded-lg bg-sky-500 text-black text-xs font-black flex items-center gap-1"><Share2 className="w-3 h-3" /> Telegram</button>
              <button
                onClick={() => {
                  const link = `${window.location.origin}/anant-khata?ref=${config?.profile?.username || ''}`;
                  const body = `📒 Anant Khata — Digital daily ledger. Try free 30 days!`;
                  window.open(shareTargets('Anant Khata', body, link).facebook, '_blank');
                }}
                data-testid="khata-share-fb"
                className="px-2 py-1 rounded-lg bg-blue-600 text-white text-xs font-black flex items-center gap-1"><Facebook className="w-3 h-3" /> FB</button>
              <button
                onClick={() => {
                  const link = `${window.location.origin}/anant-khata?ref=${config?.profile?.username || ''}`;
                  const body = `📒 Anant Khata — Digital daily ledger. Free trial!`;
                  window.open(shareTargets('Anant Khata', body, link).twitter, '_blank');
                }}
                data-testid="khata-share-tw"
                className="px-2 py-1 rounded-lg bg-black text-white text-xs font-black flex items-center gap-1"><Twitter className="w-3 h-3" /> X</button>
            </div>
          </div>
        )}

        {/* Role dashboards */}
        {hasRole && activeSub && role === 'household' && <HouseholdDashboard config={config} />}
        {hasRole && activeSub && role === 'vendor' && <VendorDashboard config={config} />}

        {/* Trust footer */}
        <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/50 p-3 flex items-center gap-2 text-[10px] text-slate-400 justify-center">
          <ShieldCheck className="w-3 h-3" /> Anant Khata · Strictly NO Affiliate Network · Direct 50 AT referral · Powered by Anant Web Portal
        </div>
      </div>
    </div>
  );
}

// ============ HOUSEHOLD DASHBOARD ============
function HouseholdDashboard({ config }) {
  const [entryDate, setEntryDate] = useState(todayISO());
  const [dayData, setDayData] = useState({ services: [], expenses: [] });
  const [monthSummary, setMonthSummary] = useState(null);
  const [expForm, setExpForm] = useState({ category: 'grocery', amount: '', note: '' });
  const [vacForm, setVacForm] = useState({ start_date: todayISO(), end_date: todayISO(), services: [], note: '' });
  const [showVacation, setShowVacation] = useState(false);
  const [vacations, setVacations] = useState([]);

  const load = useCallback(async () => {
    try {
      const [d, s, v] = await Promise.all([
        api.get('/anant-khata/household/day', { params: { entry_date: entryDate } }),
        api.get('/anant-khata/household/summary', { params: { month: monthISO() } }),
        api.get('/anant-khata/household/vacation'),
      ]);
      setDayData(d.data);
      setMonthSummary(s.data);
      setVacations(v.data?.vacations || []);
    } catch { /* silent */ }
  }, [entryDate]);
  useEffect(() => { load(); }, [load]);

  const services = config?.household_services || [];
  const receivedMap = useMemo(() => {
    const m = {};
    (dayData?.services || []).forEach((s) => { m[s.service_key] = s.received; });
    return m;
  }, [dayData]);

  const toggleService = async (key) => {
    const next = !receivedMap[key];
    try {
      await api.post('/anant-khata/household/service-toggle', { entry_date: entryDate, service_key: key, received: next });
      await load();
    } catch { toast.error('Toggle failed'); }
  };

  const addExpense = async () => {
    if (!expForm.amount || parseFloat(expForm.amount) <= 0) { toast.error('Amount required'); return; }
    try {
      await api.post('/anant-khata/household/expense', { entry_date: entryDate, category: expForm.category, amount: parseFloat(expForm.amount), note: expForm.note });
      setExpForm({ category: 'grocery', amount: '', note: '' });
      toast.success('Expense added');
      await load();
    } catch { toast.error('Failed'); }
  };
  const delExpense = async (id) => {
    try { await api.delete(`/anant-khata/household/expense/${id}`); await load(); }
    catch { toast.error('Delete failed'); }
  };

  const addVacation = async () => {
    try {
      await api.post('/anant-khata/household/vacation', vacForm);
      toast.success('Vacation mode saved · vendors will be notified');
      setShowVacation(false);
      await load();
    } catch { toast.error('Failed'); }
  };
  const delVacation = async (id) => {
    try { await api.delete(`/anant-khata/household/vacation/${id}`); await load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="khata-household-dash">
      {/* Date + Daily services */}
      <div className="rounded-2xl border-2 border-amber-400/30 bg-slate-900/60 p-4 mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-amber-300 font-black flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Daily Services Tracker</div>
          <div className="flex items-center gap-2">
            <Calendar className="w-3 h-3 opacity-60" />
            <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} data-testid="khata-date" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
            <button onClick={() => setShowVacation(true)} data-testid="khata-vacation-btn" className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-300 font-black flex items-center gap-1"><PauseCircle className="w-3 h-3" /> Vacation Mode</button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {services.map((s) => (
            <button key={s.key} onClick={() => toggleService(s.key)} data-testid={`khata-svc-${s.key}`} className={`px-3 py-2 rounded-xl border-2 text-left transition-all ${receivedMap[s.key] ? 'border-emerald-400 bg-emerald-500/15' : 'border-slate-700 bg-slate-800/50 hover:border-amber-400/60'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">{s.label}</span>
                {receivedMap[s.key] ? <Check className="w-4 h-4 text-emerald-300" /> : <X className="w-4 h-4 opacity-30" />}
              </div>
              <div className="text-[10px] opacity-70">{receivedMap[s.key] ? 'Received today' : 'Tap to mark'}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Expense entry */}
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 mb-4">
        <div className="text-xs uppercase tracking-widest text-orange-300 font-black mb-2">💸 Add Expense — {entryDate}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <select value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} data-testid="khata-exp-cat" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
            {['grocery', 'electricity', 'water_bill', 'internet', 'fuel', 'rent', 'medicine', 'other'].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input type="number" placeholder="Amount ₹" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="khata-exp-amt" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input placeholder="Note (optional)" value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} data-testid="khata-exp-note" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs col-span-2 sm:col-span-1" />
          <button onClick={addExpense} data-testid="khata-exp-add" className="px-3 py-1.5 rounded bg-orange-500 text-black text-xs font-black flex items-center justify-center gap-1"><Plus className="w-3 h-3" /> Add</button>
        </div>
        {(dayData?.expenses || []).length > 0 && (
          <div className="mt-3 space-y-1">
            {(dayData?.expenses || []).map((e) => (
              <div key={e.id} className="flex items-center gap-2 px-2 py-1 rounded bg-slate-800/60 text-xs">
                <div className="w-20 text-[10px] uppercase opacity-70">{e.category}</div>
                <div className="flex-1 truncate opacity-80">{e.note || '—'}</div>
                <div className="font-black text-orange-300">₹{e.amount}</div>
                <button onClick={() => delExpense(e.id)} className="text-red-300 opacity-60 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Auto-sync magic: vendor entries linked to this household */}
      {(dayData?.synced_vendor_entries || []).length > 0 && (
        <div className="rounded-2xl border-2 border-emerald-400/30 bg-emerald-500/5 p-4 mb-4" data-testid="khata-synced-entries">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-300 font-black flex items-center gap-1"><Sparkles className="w-3 h-3" /> ✨ Auto-Synced from Vendors</div>
              <div className="text-[10px] opacity-80">Your linked vendors&apos; entries appear here automatically — no manual typing.</div>
            </div>
            <div className="text-sm font-black text-emerald-300">₹{dayData.synced_total || 0}</div>
          </div>
          <div className="space-y-1">
            {(dayData.synced_vendor_entries || []).map((e) => (
              <div key={e.id} className="flex items-center gap-2 px-2 py-1 rounded bg-slate-800/60 text-xs">
                <div className="w-20 text-[10px] uppercase text-emerald-300 opacity-80 truncate">{e.vendor_username || 'vendor'}</div>
                <div className="flex-1 text-[10px] opacity-70 truncate">{Object.entries(e.data || {}).map(([k, v]) => `${k}:${v}`).join(' · ') || '—'}</div>
                <div className="font-black text-emerald-300">₹{e.amount}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Share day summary */}
      <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/5 p-3 mb-4 flex items-center justify-between flex-wrap gap-2">
        <div className="text-[10px] opacity-80">📤 Share today&apos;s summary on social media</div>
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => {
              const svcCount = Object.values(receivedMap).filter(Boolean).length;
              const expTotal = (dayData?.expenses || []).reduce((a, e) => a + parseFloat(e.amount || 0), 0);
              const body = `📒 Mera aaj ka Khata (${entryDate}):\n✅ ${svcCount} daily services received\n💸 ₹${expTotal.toFixed(0)} expenses\n${dayData?.synced_total ? `📥 ₹${dayData.synced_total} vendor auto-synced\n` : ''}\nDigital Khata banaya jaao — Anant Khata!`;
              shareEverywhere('Anant Khata — Daily', body, `${window.location.origin}/anant-khata`);
            }}
            data-testid="khata-share-day"
            className="px-2 py-1 rounded bg-white/10 text-xs font-black flex items-center gap-1"><Share2 className="w-3 h-3" /> Share</button>
          <button
            onClick={() => {
              const svcCount = Object.values(receivedMap).filter(Boolean).length;
              const expTotal = (dayData?.expenses || []).reduce((a, e) => a + parseFloat(e.amount || 0), 0);
              const text = `📒 Aaj ka Khata (${entryDate})\n✅ ${svcCount} services\n💸 ₹${expTotal.toFixed(0)}\n👉 ${window.location.origin}/anant-khata`;
              window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
            }}
            data-testid="khata-share-day-wa"
            className="px-2 py-1 rounded bg-green-500 text-black text-xs font-black flex items-center gap-1"><MessageCircle className="w-3 h-3" /> WA</button>
        </div>
      </div>

      {/* Monthly summary */}
      {monthSummary && (
        <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 mb-4" data-testid="khata-summary">
          <div className="flex items-center justify-between mb-2 flex-wrap">
            <div className="text-xs uppercase tracking-widest text-amber-300 font-black flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {monthSummary.month} Summary</div>
            <div className="text-lg font-black text-amber-300">₹{monthSummary.total_expense}</div>
          </div>
          {monthSummary.by_category?.length ? (
            <div className="space-y-1">
              {monthSummary.by_category.map((c) => {
                const pct = monthSummary.total_expense ? (c.amount / monthSummary.total_expense * 100).toFixed(1) : 0;
                return (
                  <div key={c.category} className="text-xs">
                    <div className="flex justify-between mb-0.5"><span className="opacity-80">{c.category}</span><span className="font-bold">₹{c.amount} <span className="opacity-60">({pct}%)</span></span></div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-500 to-orange-500" style={{ width: `${pct}%` }}></div></div>
                  </div>
                );
              })}
            </div>
          ) : <div className="text-xs opacity-60">No expenses this month yet.</div>}
          {monthSummary.service_days?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-700">
              <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">Service Days</div>
              <div className="flex flex-wrap gap-1">
                {monthSummary.service_days.map((s) => (<span key={s.service} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-200 font-black">{s.service}: {s.days}d</span>))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vacations list */}
      {vacations.length > 0 && (
        <div className="rounded-2xl border border-red-400/30 bg-red-500/5 p-3 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-red-300 font-black mb-2">⏸️ Vacation Mode</div>
          <div className="space-y-1">
            {vacations.map((v) => (
              <div key={v.id} className="flex items-center gap-2 text-xs">
                <div className="flex-1">{v.start_date} → {v.end_date} <span className="opacity-60">{v.services?.length ? `(${v.services.join(', ')})` : '(all services)'}</span></div>
                <button onClick={() => delVacation(v.id)} className="text-red-300 opacity-60 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vacation modal */}
      {showVacation && (
        <div className="fixed inset-0 bg-black/85 z-[120] flex items-center justify-center p-3" onClick={() => setShowVacation(false)}>
          <div className="w-full max-w-md rounded-2xl border-2 border-red-400/50 bg-slate-900 p-5" onClick={(e) => e.stopPropagation()} data-testid="khata-vacation-modal">
            <div className="text-sm font-black text-red-300 mb-3">⏸️ Vacation Mode</div>
            <div className="text-[11px] opacity-80 mb-3">Vendors ko notify karo ki in dates par delivery nahi chahiye.</div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input type="date" value={vacForm.start_date} onChange={(e) => setVacForm({ ...vacForm, start_date: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input type="date" value={vacForm.end_date} onChange={(e) => setVacForm({ ...vacForm, end_date: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </div>
            <input placeholder="Note (e.g. Family trip to Goa)" value={vacForm.note} onChange={(e) => setVacForm({ ...vacForm, note: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs mb-3" />
            <div className="flex gap-2">
              <button onClick={() => setShowVacation(false)} className="flex-1 px-3 py-2 rounded-lg bg-slate-700 text-xs">Cancel</button>
              <button onClick={addVacation} data-testid="khata-vac-save" className="flex-1 px-3 py-2 rounded-lg bg-red-500 text-white text-xs font-black">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ VENDOR DASHBOARD ============
function VendorDashboard({ config }) {
  const [customers, setCustomers] = useState([]);
  const [entries, setEntries] = useState([]);
  const [entryDate, setEntryDate] = useState(todayISO());
  const [month, setMonth] = useState(monthISO());
  const [monthTotal, setMonthTotal] = useState(0);
  const [showAddCust, setShowAddCust] = useState(false);
  const [custForm, setCustForm] = useState({ name: '', phone: '', address: '', default_qty: '', default_rate: '', active: true, linked_household_username: '' });
  const [bulkMap, setBulkMap] = useState({});   // {customer_id: {data:{qty,rate}, amount}}
  const [selectedCust, setSelectedCust] = useState(null);
  const [statement, setStatement] = useState(null);

  const catKey = config?.profile?.vendor_category || 'milk';
  const category = useMemo(() => (config?.vendor_categories || []).find((c) => c.key === catKey) || (config?.vendor_categories || [])[0], [config, catKey]);
  const columns = category?.columns || [];

  const load = useCallback(async () => {
    try {
      const [c, e] = await Promise.all([
        api.get('/anant-khata/customers'),
        api.get('/anant-khata/entries', { params: { month } }),
      ]);
      setCustomers(c.data?.customers || []);
      setEntries(e.data?.entries || []);
      setMonthTotal(e.data?.total_amount || 0);
    } catch { /* silent */ }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const addCustomer = async () => {
    if (!custForm.name.trim()) { toast.error('Name required'); return; }
    try {
      const payload = { ...custForm, default_qty: parseFloat(custForm.default_qty) || 0, default_rate: parseFloat(custForm.default_rate) || 0 };
      await api.post('/anant-khata/customers', payload);
      toast.success(payload.linked_household_username ? `Customer added + linked to ${payload.linked_household_username} (auto-sync ON ✨)` : 'Customer added');
      setCustForm({ name: '', phone: '', address: '', default_qty: '', default_rate: '', active: true, linked_household_username: '' });
      setShowAddCust(false);
      await load();
    } catch { toast.error('Failed'); }
  };
  const delCustomer = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try { await api.delete(`/anant-khata/customers/${id}`); toast.success('Deleted'); await load(); }
    catch { toast.error('Failed'); }
  };

  const updateBulkCell = (cid, field, val) => {
    setBulkMap((m) => {
      const cur = m[cid] || { data: {}, amount: 0 };
      const nextData = field === 'amount' ? cur.data : { ...cur.data, [field]: val };
      const nextAmount = field === 'amount' ? val : cur.amount;
      // Auto-compute amount when qty * rate present
      let amt = parseFloat(nextAmount || 0);
      const qty = parseFloat(nextData.qty_l ?? nextData.qty ?? nextData.cans ?? 0);
      const rate = parseFloat(nextData.rate ?? 0);
      if (!nextAmount && qty && rate) amt = qty * rate;
      return { ...m, [cid]: { data: nextData, amount: amt } };
    });
  };

  const saveBulk = async () => {
    const rows = Object.entries(bulkMap).filter(([, v]) => (v.amount || 0) > 0 || Object.keys(v.data || {}).length > 0).map(([customer_id, v]) => ({ customer_id, data: v.data, amount: v.amount || 0 }));
    if (rows.length === 0) { toast.error('Nothing to save'); return; }
    try {
      const r = await api.post('/anant-khata/entries/bulk', { entry_date: entryDate, entries: rows });
      toast.success(`${r.data.inserted} entries saved`);
      setBulkMap({});
      await load();
    } catch { toast.error('Failed'); }
  };

  const openStatement = async (cust) => {
    setSelectedCust(cust);
    try {
      const r = await api.get('/anant-khata/statement', { params: { customer_id: cust.id, month } });
      setStatement(r.data);
    } catch { toast.error('Failed to load statement'); }
  };

  const downloadStatementPDF = async (cust, stmt) => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const shopName = config?.profile?.shop_name || 'Anant Khata';
      let y = 15;
      doc.setFontSize(18); doc.setTextColor(200, 100, 20);
      doc.text(shopName, 15, y); y += 7;
      doc.setFontSize(10); doc.setTextColor(80);
      doc.text('Monthly Statement — Anant Khata', 15, y); y += 10;
      doc.setDrawColor(200); doc.line(15, y - 4, 195, y - 4);
      doc.setFontSize(11); doc.setTextColor(20);
      doc.text(`Customer: ${cust.name}`, 15, y); y += 5;
      if (cust.phone) { doc.text(`Phone: ${cust.phone}`, 15, y); y += 5; }
      if (cust.address) { doc.text(`Address: ${cust.address}`, 15, y); y += 5; }
      doc.text(`Month: ${stmt.month}   ·   Entries: ${stmt.count}`, 15, y); y += 8;
      doc.setFontSize(10); doc.setTextColor(60);
      // Table header
      doc.setFillColor(255, 200, 120); doc.rect(15, y - 4, 180, 6, 'F');
      doc.setTextColor(20); doc.text('Date', 18, y);
      doc.text('Details', 55, y);
      doc.text('Amount (Rs.)', 165, y); y += 6;
      doc.setFontSize(9); doc.setTextColor(50);
      stmt.entries.forEach((e) => {
        if (y > 265) { doc.addPage(); y = 15; }
        const details = Object.entries(e.data || {}).map(([k, v]) => `${k}:${v}`).join(', ').slice(0, 60);
        doc.text(e.entry_date, 18, y);
        doc.text(details || '—', 55, y);
        doc.text(String(e.amount), 175, y);
        y += 5;
      });
      y += 4;
      doc.setDrawColor(200); doc.line(15, y, 195, y); y += 6;
      doc.setFontSize(13); doc.setTextColor(180, 90, 0);
      doc.text(`TOTAL: Rs. ${stmt.total}`, 130, y); y += 10;
      doc.setFontSize(8); doc.setTextColor(120);
      doc.text('Powered by Anant Khata · Digital Vyapar Ledger · anantportal.com/anant-khata', 15, 285);
      doc.save(`${cust.name.replace(/\s+/g, '_')}-${stmt.month}-statement.pdf`);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('PDF export failed — trying text fallback');
      const text = `${config?.profile?.shop_name || 'Vyapar'} — Statement\nCustomer: ${cust.name}\nMonth: ${stmt.month}\n\n${stmt.entries.map((e) => `${e.entry_date}: Rs.${e.amount}`).join('\n')}\n\nTotal: Rs.${stmt.total}`;
      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${cust.name}-${stmt.month}.txt`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    }
  };

  const shareStatementSocial = (cust, stmt, channel) => {
    const shopName = config?.profile?.shop_name || 'Vyapar';
    const summary = `📒 *${shopName}* — ${cust.name} ka ${stmt.month} khata:\n${stmt.count} entries · *Total ₹${stmt.total}*`;
    const url = `${window.location.origin}/anant-khata`;
    const targets = shareTargets(`${shopName} Statement`, summary, url);
    if (channel === 'copy') { navigator.clipboard.writeText(`${summary}\n${url}`); toast.success('Copied'); return; }
    window.open(targets[channel], '_blank');
  };

  const sendWhatsAppReminder = (cust, stmt) => {
    if (!cust.phone) { toast.error('Customer phone not set'); return; }
    const num = cust.phone.replace(/\D/g, '');
    const shopName = config?.profile?.shop_name || 'Vyapar';
    const text = `Namaste ${cust.name} ji,\n\n📒 *${shopName}* — ${stmt.month} khaata statement:\n\n${stmt.entries.slice(0, 10).map((e) => `${e.entry_date}: ₹${e.amount}`).join('\n')}${stmt.count > 10 ? `\n… +${stmt.count - 10} more entries` : ''}\n\n*Total: ₹${stmt.total}*\n\nBharosa banaye rakhne ke liye dhanyawaad 🙏`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div data-testid="khata-vendor-dash">
      {/* Header */}
      <div className="rounded-2xl border-2 border-orange-400/30 bg-slate-900/60 p-4 mb-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-orange-300 font-black">🏪 {config?.profile?.shop_name || 'Vyapar Khata'} · {category?.label}</div>
          <div className="text-xs opacity-80 mt-0.5">{customers.length} customers · {entries.length} entries in {month} · Total <b className="text-orange-300">₹{monthTotal.toFixed(2)}</b></div>
        </div>
        <div className="flex gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
          <button onClick={() => setShowAddCust(true)} data-testid="khata-cust-add" className="px-3 py-1 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-black flex items-center gap-1"><Plus className="w-3 h-3" /> Customer</button>
        </div>
      </div>

      {/* Bulk entry table */}
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 mb-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-orange-300 font-black flex items-center gap-1"><Calendar className="w-3 h-3" /> Route Entry · {entryDate}</div>
          <div className="flex items-center gap-2">
            <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} data-testid="khata-entry-date" className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
            <button onClick={saveBulk} data-testid="khata-bulk-save" className="px-3 py-1 rounded bg-emerald-500 text-black text-xs font-black">✓ Save All</button>
          </div>
        </div>
        {customers.length === 0 ? (
          <div className="text-center py-6 text-xs text-slate-400">No customers yet. Add your first one from the top-right button.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left border-b border-slate-700">
                  <th className="py-1 px-1">Customer</th>
                  {columns.map((c) => <th key={c.key} className="py-1 px-1">{c.label}</th>)}
                  <th className="py-1 px-1">Amount ₹</th>
                </tr>
              </thead>
              <tbody>
                {customers.filter((c) => c.active !== false).map((c) => (
                  <tr key={c.id} className="border-b border-slate-800">
                    <td className="py-1 px-1"><div className="font-bold">{c.name}</div><div className="text-[9px] opacity-60">{c.phone || '—'}</div></td>
                    {columns.map((col) => (
                      <td key={col.key} className="py-1 px-1">
                        <input
                          type={col.type === 'number' ? 'number' : 'text'}
                          value={bulkMap[c.id]?.data?.[col.key] ?? (col.key === 'qty_l' || col.key === 'qty' || col.key === 'cans' ? c.default_qty || '' : col.key === 'rate' ? c.default_rate || '' : '')}
                          onChange={(e) => updateBulkCell(c.id, col.key, e.target.value)}
                          data-testid={`khata-cell-${c.id}-${col.key}`}
                          className="w-16 px-1 py-0.5 rounded bg-slate-800 border border-slate-700 text-xs"
                        />
                      </td>
                    ))}
                    <td className="py-1 px-1">
                      <input type="number" value={bulkMap[c.id]?.amount || ''} onChange={(e) => updateBulkCell(c.id, 'amount', e.target.value)} placeholder="auto" data-testid={`khata-amt-${c.id}`} className="w-20 px-1 py-0.5 rounded bg-slate-800 border border-orange-500/40 text-xs font-black text-orange-300" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customers list */}
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 mb-4">
        <div className="text-xs uppercase tracking-widest text-orange-300 font-black mb-2">👥 Customer List</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {customers.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-700 bg-slate-800/60 p-2 flex items-center gap-2" data-testid={`khata-cust-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black">{c.name} {c.active === false && <span className="text-[9px] text-red-300 ml-1">PAUSED</span>}</div>
                <div className="text-[10px] opacity-70">{c.phone || 'No phone'} · {c.address || 'No address'}</div>
              </div>
              <button onClick={() => openStatement(c)} data-testid={`khata-stmt-${c.id}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-black">Statement</button>
              <button onClick={() => delCustomer(c.id)} className="text-red-300 opacity-60 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Add customer modal */}
      {showAddCust && (
        <div className="fixed inset-0 bg-black/85 z-[120] flex items-center justify-center p-3" onClick={() => setShowAddCust(false)}>
          <div className="w-full max-w-md rounded-2xl border-2 border-orange-400/50 bg-slate-900 p-5 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="khata-cust-modal">
            <div className="text-sm font-black text-orange-300">+ New Customer</div>
            <input placeholder="Customer Name *" value={custForm.name} onChange={(e) => setCustForm({ ...custForm, name: e.target.value })} data-testid="khata-cust-name" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input placeholder="Phone (for WhatsApp bills)" value={custForm.phone} onChange={(e) => setCustForm({ ...custForm, phone: e.target.value })} data-testid="khata-cust-phone" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input placeholder="Address / Route Info" value={custForm.address} onChange={(e) => setCustForm({ ...custForm, address: e.target.value })} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/5 p-2">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> ✨ Auto-Sync Magic (Optional)</div>
              <input placeholder="Household username on Anant Khata" value={custForm.linked_household_username} onChange={(e) => setCustForm({ ...custForm, linked_household_username: e.target.value })} data-testid="khata-cust-link" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono" />
              <div className="text-[9px] opacity-70 mt-1">If this customer is also using Anant Khata as Household, entries auto-appear in their app.</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" placeholder="Default Qty" value={custForm.default_qty} onChange={(e) => setCustForm({ ...custForm, default_qty: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input type="number" placeholder="Default Rate ₹" value={custForm.default_rate} onChange={(e) => setCustForm({ ...custForm, default_rate: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setShowAddCust(false)} className="flex-1 px-3 py-2 rounded-lg bg-slate-700 text-xs">Cancel</button>
              <button onClick={addCustomer} data-testid="khata-cust-save" className="flex-1 px-3 py-2 rounded-lg bg-orange-500 text-white text-xs font-black">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Statement modal */}
      {selectedCust && statement && (
        <div className="fixed inset-0 bg-black/85 z-[120] flex items-center justify-center p-3" onClick={() => { setSelectedCust(null); setStatement(null); }}>
          <div className="w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl border-2 border-emerald-400/50 bg-slate-900 p-5" onClick={(e) => e.stopPropagation()} data-testid="khata-stmt-modal">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-black text-emerald-300">{selectedCust.name}</div>
                <div className="text-[10px] opacity-70">{statement.month} · {statement.count} entries</div>
              </div>
              <div className="text-lg font-black text-emerald-300">₹{statement.total}</div>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1 my-3">
              {statement.entries.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-xs px-2 py-1 rounded bg-slate-800/60">
                  <div className="w-24 opacity-70">{e.entry_date}</div>
                  <div className="flex-1 text-[10px] opacity-80 truncate">{JSON.stringify(e.data)}</div>
                  <div className="font-black">₹{e.amount}</div>
                </div>
              ))}
              {statement.entries.length === 0 && <div className="text-xs opacity-60 text-center py-4">No entries for this month.</div>}
            </div>
            <button onClick={() => sendWhatsAppReminder(selectedCust, statement)} disabled={!selectedCust.phone} data-testid="khata-wa-send" className="w-full px-3 py-2 rounded-lg bg-green-500 text-black text-xs font-black flex items-center justify-center gap-1 disabled:opacity-40"><MessageCircle className="w-3 h-3" /> Send Bill via WhatsApp</button>
            {!selectedCust.phone && <div className="text-[10px] text-red-300 text-center mt-1">Customer phone not set — edit customer to add.</div>}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button onClick={() => downloadStatementPDF(selectedCust, statement)} data-testid="khata-stmt-pdf" className="px-3 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-black flex items-center justify-center gap-1"><Download className="w-3 h-3" /> Download PDF</button>
              <button onClick={() => shareStatementSocial(selectedCust, statement, 'copy')} data-testid="khata-stmt-copy" className="px-3 py-2 rounded-lg bg-white/10 text-xs font-black flex items-center justify-center gap-1"><Copy className="w-3 h-3" /> Copy Summary</button>
            </div>
            <div className="flex gap-1 mt-2 flex-wrap justify-center">
              <button onClick={() => shareStatementSocial(selectedCust, statement, 'telegram')} data-testid="khata-stmt-tg" className="px-2 py-1 rounded bg-sky-500 text-black text-[10px] font-black flex items-center gap-1"><Share2 className="w-2.5 h-2.5" /> Telegram</button>
              <button onClick={() => shareStatementSocial(selectedCust, statement, 'facebook')} data-testid="khata-stmt-fb" className="px-2 py-1 rounded bg-blue-600 text-white text-[10px] font-black flex items-center gap-1"><Facebook className="w-2.5 h-2.5" /> Facebook</button>
              <button onClick={() => shareStatementSocial(selectedCust, statement, 'twitter')} data-testid="khata-stmt-tw" className="px-2 py-1 rounded bg-black text-white text-[10px] font-black flex items-center gap-1"><Twitter className="w-2.5 h-2.5" /> X (Twitter)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
